import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactVideoRecorder from 'react-video-recorder';

import 'common/recorder/video-recorder/VideoRecorder.scss';
import { useProfile } from 'auth/Auth';
import { AccessPolicy } from 'common/assets/Assets';
import { blobToFile, formatTime, useTimer, useCountDown } from 'common/recorder/Util';
import { UploadedFileAsset } from 'common/upload/Upload';
import { Uploader } from 'common/upload/uploader/Uploader';
import { ButtonVariant } from 'core/util/Bootstrap';

interface CloudUploadArgs {
    shortenExternalUrl?: boolean;
    accessPolicy?: AccessPolicy;
    expireDate?: number | null;
    moduleId?: string;
}

interface CloudUploadOptions {
    enable: boolean;
    onUpload?(uploadedAsset: UploadedFileAsset): any;
    options?: CloudUploadArgs;
    maxVideoSize?: number;
}

interface Props {
    closeVideoRecorder(): any;
    maxTimeLimit?: number;
    onVideoRecord?(file: File): any;
    uploadToCloud?: CloudUploadOptions;
    showCounterBeforeRecord?: boolean;
    countdownTimer?: number;
}

enum RecordingState {
    Inital = 0,
    Start = 1,
    Stop = 2,
    Done = 3,
    Pause = 4,
    Resume = 5,
    Retake = 6,
    Error = 7,
    Countdown = 8,
}

export const VideoRecorder: FunctionComponent<Props> = ({
    closeVideoRecorder,
    maxTimeLimit,
    onVideoRecord,
    uploadToCloud,
    showCounterBeforeRecord,
    countdownTimer,
}) => {
    const [recordingState, setRecordingState] = useState<number>(0);
    const videoRecorderRef = useRef<any | null>(null);
    const { timer, handleStart, handlePause, handleResume, handleReset } = useTimer(0);
    const componentWillUnmount = useRef(false);
    const [videoOutput, setVideoOutput] = useState<Blob | null>(null);
    const [{ profile }] = useProfile();
    const { t } = useTranslation(['common/video-recorder']);
    const uploaderRef = useRef(null) as any;
    const { countDown, startCountDown, cancelCountdown } = useCountDown(countdownTimer);
    const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);

    const handleStartRecording = useCallback((): any => {
        setRecordingState(RecordingState.Start);
        videoRecorderRef.current.handleStartRecording();
        handleStart();
    }, [handleStart]);

    useEffect(() => {
        if (countDown === 0) {
            handleStartRecording();
        }
    }, [countDown, handleStartRecording]);

    const videoBlob = (blob: Blob): void => {
        setVideoOutput(blob);
    };

    const disconnectedView = (): any => {
        videoRecorderRef.current.turnOnCamera();
        return <div>{t('DISCONNECTED_VIEW_MESSAGE')}</div>;
    };

    const loadingView = (): any => {
        return <div>{t('LOADING_VIEW_MESSAGE')}</div>;
    };

    const unsupportedView = (): any => {
        return <div>{t('UNSUPPORTED_VIEW_MESSAGE')}</div>;
    };

    const reTryCamera = (): any => {
        handleReset();
        videoRecorderRef.current.turnOnCamera();
    };

    const errorView = (): any => {
        setRecordingState(RecordingState.Error);
        return (
            <div>
                {t('ERROR_VIEW_MESSAGE')}
                <div>
                    <button type="button" onClick={reTryCamera}>
                        {t('TRY_NOW')}
                    </button>
                </div>
            </div>
        );
    };

    const handleTurnOnCamera = (): any => {
        setRecordingState(RecordingState.Inital);
    };

    const handleStopRecording = (): any => {
        setRecordingState(RecordingState.Done);
        videoRecorderRef.current.handleStopRecording();
        videoRecorderRef.current.handleStop();
        handleReset();
    };

    const handlePauseRecording = (): any => {
        setRecordingState(RecordingState.Pause);
        videoRecorderRef.current.handlePauseRecording();
        handlePause();
    };

    const handleResumeRecording = (): any => {
        setRecordingState(RecordingState.Resume);
        videoRecorderRef.current.handleResumeRecording();
        handleResume();
    };

    const confirmRetakeVideo = (): any => {
        setRecordingState(RecordingState.Retake);
    };

    const retakeVideo = (): any => {
        setRecordingState(RecordingState.Inital);
        setVideoOutput(null);
        videoRecorderRef.current.turnOnCamera();
    };

    const uploadVideo = (): any => {
        if (videoOutput) {
            const fileName = `${profile?.tenant?.agencyID}-${profile?.tenant?.id}-${
                profile!.user.id
            }_Video_Recording_${new Date().getTime()}.webm`;
            const file = blobToFile(videoOutput, fileName);

            if (uploadToCloud && uploadToCloud.enable) {
                uploadVideoTos3(file);
            } else if (videoOutput && onVideoRecord) {
                onVideoRecord!(file);
            }
        }
    };

    const uploadVideoTos3 = async (file: any): Promise<any> => {
        await uploaderRef.current.addAcceptedFiles([file]);
    };

    const cancelRetake = (): any => {
        setRecordingState(RecordingState.Done);
    };

    const handleStartCountdown = (): void => {
        startCountDown();
        setRecordingState(RecordingState.Countdown);
    };

    function handleCancelCountDown(): void {
        cancelCountdown();
        setRecordingState(RecordingState.Inital);
    }

    const renderActionButtons = (): any => {
        if (recordingState === RecordingState.Inital) {
            return (
                <div className="action-buttons-inner-cntr single-action-button-cntr">
                    <button
                        type="button"
                        className="record-button action-button"
                        onClick={handleStartCountdown}
                        aria-label="Video Record"
                    >
                        <div className="record-icon" />
                    </button>
                </div>
            );
        }

        if (recordingState === RecordingState.Countdown) {
            return (
                <div className="video-recording-done">
                    <div className="action-left-btn" onClick={handleCancelCountDown} aria-hidden="true">
                        {t('CANCEL')}
                    </div>
                </div>
            );
        }

        if (
            recordingState === RecordingState.Start ||
            recordingState === RecordingState.Pause ||
            recordingState === RecordingState.Resume
        ) {
            return (
                <div className="action-buttons-inner-cntr actions-flex multi-action-buttons-cntr">
                    {toggleResumePauseIcons()}
                    <button className="action-button action-stop-button" type="button" onClick={handleStopRecording}>
                        <i className="icon-stop" />
                        <div>{t('STOP')}</div>
                    </button>
                </div>
            );
        }

        if (recordingState === RecordingState.Done) {
            return (
                <div className="video-recording-done">
                    <div className="action-left-btn" onClick={confirmRetakeVideo} aria-hidden="true">
                        {t('RETAKE_RECORDING')}
                    </div>
                    <div className="action-right-btn" onClick={uploadVideo} aria-hidden="true">
                        {t('USE_THIS_RECORDING')}
                    </div>
                </div>
            );
        }

        if (recordingState === RecordingState.Retake) {
            return (
                <div className="video-recording-done">
                    <div className="retake-confirm-msg">{t('CONFIRM_RETAKE_RECORDING_MESSAGE')}</div>
                    <div className="action-left-btn left-btn-margin" onClick={cancelRetake} aria-hidden="true">
                        {t('CANCEL')}
                    </div>
                    <div className="action-right-btn right-btn-margin" onClick={retakeVideo} aria-hidden="true">
                        {t('RETAKE')}
                    </div>
                </div>
            );
        }

        return '';
    };

    const toggleResumePauseIcons = (): any => {
        if (recordingState === RecordingState.Pause) {
            return (
                <button className="action-button" type="button" onClick={handleResumeRecording}>
                    <i className="icon-play" />
                    <div>{t('CONTINUE')}</div>
                </button>
            );
        }

        if (recordingState === RecordingState.Resume || recordingState === RecordingState.Start) {
            return (
                <button className="action-button" type="button" onClick={handlePauseRecording}>
                    <i className="icon-pause" />
                    <div>{t('PAUSE')}</div>
                </button>
            );
        }

        return '';
    };

    const handlemaxTimeLimit = (): any => {
        if (maxTimeLimit) {
            return `/ ${formatTime(maxTimeLimit - timer)}`;
        }
        return '';
    };

    useEffect(() => {
        if (maxTimeLimit) {
            if (timer > maxTimeLimit) {
                setRecordingState(RecordingState.Done);
                videoRecorderRef.current.handleStopRecording();
                handleReset();
            }
        }
    }, [timer, handleReset, maxTimeLimit]);

    useEffect(() => {
        return (): void => {
            componentWillUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        let videoCurrRef = videoRecorderRef.current;

        return (): void => {
            if (componentWillUnmount.current) {
                if (videoCurrRef.mediaRecorder && videoCurrRef.mediaRecorder.state !== 'inactive') {
                    videoCurrRef.handleStopRecording();
                    videoCurrRef = null;
                }
                handleReset();
            }
        };
    }, [handleReset]);

    const handleUploadFile = (uploadedAssets: any): void => {
        const uploadedAsset = uploadedAssets[0];

        if (uploadedAsset && uploadToCloud && uploadToCloud?.onUpload) {
            uploadToCloud.onUpload(uploadedAsset);
        }
    };

    function renderCountDown(): any {
        if (recordingState === RecordingState.Countdown) {
            return <div className="countdown">{t('COUNTDOWN_MESSAGE', { countdown: countDown })}</div>;
        }
        return null;
    }

    function handleCloseIcon(): any {
        if (recordingState === RecordingState.Inital) {
            return closeVideoRecorder();
        }
        return setShowCancelConfirmationModal(true);
    }

    function renderInitiateRecordMessage(): any {
        if (recordingState === RecordingState.Inital) {
            return (
                <div className="start-recording-message">
                    {t('HIT')} &nbsp; <span className="red-text">{t('RECORD')}</span> &nbsp; {t('TO_START')}
                </div>
            );
        }
        return null;
    }

    return (
        <div className="r4e-video-recorder">
            <div className="record-container">
                <div className="title">{t('RECORD_VIDEO')}</div>
                <div className="video-recorder-container">
                    {recordingState !== RecordingState.Done && recordingState !== RecordingState.Error ? (
                        <div className="timer">
                            <div className="timer-dot-icon" />
                            <div className="timer-text">
                                {formatTime(timer)} {handlemaxTimeLimit()}
                            </div>
                        </div>
                    ) : (
                        ''
                    )}
                    <ReactVideoRecorder
                        chunkSize={250}
                        constraints={{
                            audio: true,
                            video: true,
                        }}
                        onRecordingComplete={videoBlob}
                        countdownTime={0}
                        renderActions={(props: any): any => <></>} // Returning Fragment as renderActions expects a element as it's return value
                        ref={videoRecorderRef}
                        onCameraOn={handleTurnOnCamera}
                        onTurnOnCamera={handleTurnOnCamera}
                        renderDisconnectedView={disconnectedView}
                        renderLoadingView={loadingView}
                        renderUnsupportedView={unsupportedView}
                        renderErrorView={errorView}
                        replayVideoAutoplayAndLoopOff
                        showReplayControls
                    />
                    {renderCountDown()}
                    {renderInitiateRecordMessage()}
                    <div className="action-buttons-container">{renderActionButtons()}</div>
                </div>
                <div className="close-icon" onClick={handleCloseIcon} aria-hidden="true">
                    <i className="icon-times-circle" />
                </div>
                {uploadToCloud?.enable && (
                    <Uploader
                        agencyId={profile?.tenant?.agencyID!}
                        tenantId={profile?.tenant?.id!}
                        uploadToCloud
                        ref={uploaderRef}
                        maxSizePerFile={uploadToCloud?.maxVideoSize}
                        maxSizeTotal={uploadToCloud?.maxVideoSize}
                        displayProgressBar
                        onFilesAccepted={handleUploadFile}
                        cancelUpload
                        accessPolicy={uploadToCloud?.options?.accessPolicy}
                        shortenExternalUrl={uploadToCloud?.options?.shortenExternalUrl}
                        expireDate={uploadToCloud?.options?.expireDate}
                        moduleId={uploadToCloud?.options?.moduleId}
                    >
                        {null}
                    </Uploader>
                )}
            </div>
            <Modal show={showCancelConfirmationModal} centered>
                <Modal.Header onHide={(): void => setShowCancelConfirmationModal(false)} closeButton>
                    <Modal.Title>{t('CANCEL_MODAL_TITLE')}</Modal.Title>
                </Modal.Header>
                <Modal.Body bsPrefix="modal-body r4e-video-recorder-cancel-modal-body">
                    <p>{t('CANCEL_MODAL_MESSAGE')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={(): void => setShowCancelConfirmationModal(false)} variant={ButtonVariant.Primary}>
                        {t('NO')}
                    </Button>
                    <Button onClick={closeVideoRecorder} variant={ButtonVariant.Secondary}>
                        {t('CANCEL_MODAL_CONFIRM_BUTTON')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};
